import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { SITE_MEDIA } from "@/constants/siteMedia";
import { HOME_SECTION_IDS } from "@/constants/homeSections";
import { navigateToHomeSection } from "@/utils/homeNavigation";

const sectionLinks = [
  { label: "Blog", href: "/blog", isSection: false },
  { label: "Pricing", href: "/pricing", isSection: true, sectionId: "pricing" },
  { label: "FAQs", href: "/faqs", isSection: true, sectionId: "faqs" },
  { label: "Trust", href: "/trust", isSection: true, sectionId: "trust" },
  { label: "Contact", href: "/contact", isSection: false },
];

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome =
    location.pathname === "/" ||
    HOME_SECTION_IDS.some((id) => location.pathname === `/${id}`);

  const handleSectionClick = useCallback(
    (e: React.MouseEvent, sectionId: string) => {
      e.preventDefault();
      navigateToHomeSection(sectionId, navigate, location.pathname);
    },
    [navigate, location.pathname]
  );

  return (
    <footer
      id="site-footer"
      className="py-10"
      style={
        isHome
          ? { background: "#ffffff" }
          : { background: "linear-gradient(160deg, #f5f0ff 0%, #ede8ff 60%, #faf8ff 100%)" }
      }
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
              >
                <img
                  src={SITE_MEDIA.avaFace512}
                  alt="AVA Chat AI Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="font-display font-bold text-[#2d1b69] text-base">AVA CHAT AI</span>
            </div>
            <p className="text-[#7a6aaa] text-xs leading-relaxed mb-3">
              Your AI companion for real conversation and emotional support. Always here, always
              listening.
            </p>
            <span className="text-[#7a6aaa] text-xs">by Neuroava LLC</span>
          </div>

          <div>
            <h4 className="font-semibold text-[#1a1040] text-sm mb-3">Quick Links</h4>
            <div className="flex flex-col gap-2">
              {sectionLinks.map((link) =>
                link.isSection ? (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={(e) => handleSectionClick(e, link.sectionId!)}
                    className="text-[#7a6aaa] hover:text-[#7C3AED] transition-colors text-sm cursor-pointer"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="text-[#7a6aaa] hover:text-[#7C3AED] transition-colors text-sm cursor-pointer"
                  >
                    {link.label}
                  </Link>
                )
              )}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-[#1a1040] text-sm mb-3">Legal</h4>
            <div className="flex flex-col gap-2">
              <Link
                to="/privacy-policy"
                className="text-[#7a6aaa] hover:text-[#7C3AED] transition-colors text-sm cursor-pointer"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-service"
                className="text-[#7a6aaa] hover:text-[#7C3AED] transition-colors text-sm cursor-pointer"
              >
                Terms of Service
              </Link>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-[#1a1040] text-sm mb-3">Contact</h4>
            <div className="flex flex-col gap-2 text-sm">
              <a
                href="tel:+16462005479"
                className="text-[#7a6aaa] hover:text-[#7C3AED] transition-colors flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-phone-line text-xs"></i>
                +1 (646) 200-5479
              </a>
              <a
                href="mailto:info@neuroava.com"
                className="text-[#7a6aaa] hover:text-[#7C3AED] transition-colors flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-mail-line text-xs"></i>
                info@neuroava.com
              </a>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3 mb-6">
          <a
            href="https://play.google.com/store/apps/details?id=com.avachat.ai.app&hl=en"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
            style={{ background: "#2d1b69" }}
            aria-label="Google Play"
          >
            <i className="ri-google-play-fill text-white text-lg"></i>
            <div className="text-left">
              <p className="text-white text-[10px] leading-none">GET IT ON</p>
              <p className="text-white text-sm font-semibold leading-tight">Google Play</p>
            </div>
          </a>
          <a
            href="https://apps.apple.com/us/app/avachatai/id6745537736"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
            style={{ background: "#2d1b69" }}
            aria-label="App Store"
          >
            <i className="ri-apple-fill text-white text-lg"></i>
            <div className="text-left">
              <p className="text-white text-[10px] leading-none">Download on the</p>
              <p className="text-white text-sm font-semibold leading-tight">App Store</p>
            </div>
          </a>
        </div>

        <div className="pt-6 border-t border-[#e0d8ff] flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[#7a6aaa] text-xs">&copy; 2026 AvachatAI. All rights reserved.</p>
          <p className="text-[#aaa] text-xs text-center max-w-md">
            AvachatAI is not a substitute for professional mental health support. If you are in
            crisis, please contact a licensed professional.
          </p>
        </div>
      </div>
    </footer>
  );
}
